import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Org } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';
import { isEqual } from 'lodash-es';
import { getUser, setLoginData } from '@frontend/auth-helpers';
import { shell, useShellLocationsSyncStore } from '@frontend/shell-utils';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { useAllAccessScopeStore } from './all-access-store';
import { WeaveLocation, WeaveLocationGroup } from './types';

export type OrgIdMap = Record<string, OrgIdMapKey>;

export type OrgIdMapKey = { parents: WeaveLocationGroup[]; locations: WeaveLocation[] };
interface ScopeStore {
  locationData: Record<string, WeaveLocation>;
  setLocationData: (scopes: Record<string, WeaveLocation>) => void;
  groupData: Record<string, WeaveLocationGroup>;
  setGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  orgData: Record<string, Org>;
  setOrgData: (orgs: Record<string, Org>) => void;
  selectedOrgId: string;
  setSelectedOrgId: (orgId: string) => void;
  selectedGroupId: string;
  setSelectedGroupId: (groupId: string) => void;
  forcedGroupId: string;
  setForcedGroupId: (groupId: string) => void;
  selectedLocationIds: string[];
  setSelectedLocationIds: (scopes: string[]) => void;
  appendLocationData: (scopes: Record<string, WeaveLocation>) => void;
  appendGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  appendOrgData: (orgs: Record<string, Org>) => void;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  setScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;
  appendScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;

  // Derived/computed properties that will be set by useAppScopeStoreWithSetters
  accessibleLocationMap: Record<string, WeaveLocation>;
  setAccessibleLocationMap: (map: Record<string, WeaveLocation>) => void;
  accessibleGroupMap: Record<string, WeaveLocationGroup>;
  setAccessibleGroupMap: (map: Record<string, WeaveLocationGroup>) => void;
  accessibleScopeHierarchy: Record<string, Record<string, string[]>>;
  setAccessibleScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;
  accessibleGroupIds: string[];
  setAccessibleGroupIds: (ids: string[]) => void;
  accessibleLocationIds: string[];
  setAccessibleLocationIds: (ids: string[]) => void;
  accessibleOrgIds: string[];
  setAccessibleOrgIds: (ids: string[]) => void;
  isSingleTypeScope: boolean;
  setIsSingleTypeScope: (value: boolean) => void;
}

export const createScopeStore = () =>
  createStoreWithSubscribe<ScopeStore>(
    (set, get) => ({
      locationData: {},
      setLocationData: (locations) => {
        set({ locationData: locations }, false, { type: 'setLocationData' });
      },
      groupData: {},
      setGroupData: (groups) => {
        set({ groupData: groups }, false, { type: 'setGroupData' });
      },
      orgData: {},
      setOrgData: (orgs) => {
        set({ orgData: orgs }, false, { type: 'setOrgData' });
      },
      selectedOrgId: '',
      setSelectedOrgId: (orgId) => {
        set({ selectedOrgId: orgId }, false, { type: 'setSelectedOrgId' });
      },
      selectedGroupId: '',
      setSelectedGroupId: (groupId) => {
        set({ selectedGroupId: groupId }, false, { type: 'setSelectedGroupId' });
      },
      forcedGroupId: '',
      setForcedGroupId: (groupId) => {
        set({ forcedGroupId: groupId }, false, { type: 'setForcedGroupId' });
      },
      selectedLocationIds: [],
      setSelectedLocationIds: (scopes) => {
        set({ selectedLocationIds: scopes }, false, { type: 'setSelectedLocationIds' });
      },
      appendLocationData: (locations) => {
        set({ locationData: { ...get().locationData, ...locations } }, false, { type: 'appendLocationData' });
      },
      appendGroupData: (groups) => {
        set({ groupData: { ...get().groupData, ...groups } }, false, { type: 'appendGroupData' });
      },
      appendOrgData: (orgs) => {
        set({ orgData: { ...get().orgData, ...orgs } }, false, { type: 'appendOrgData' });
      },
      scopeHierarchy: {},
      setScopeHierarchy: (hierarchy) => {
        set({ scopeHierarchy: hierarchy }, false, { type: 'setScopeHierarchy' });
      },
      appendScopeHierarchy: (hierarchy) => {
        set({ scopeHierarchy: { ...get().scopeHierarchy, ...hierarchy } }, false, { type: 'appendScopeHierarchy' });
      },

      // Derived/computed properties - initialized with defaults
      accessibleLocationMap: {},
      setAccessibleLocationMap: (map) => {
        set({ accessibleLocationMap: map }, false, { type: 'setAccessibleLocationMap' });
      },
      accessibleGroupMap: {},
      setAccessibleGroupMap: (map) => {
        set({ accessibleGroupMap: map }, false, { type: 'setAccessibleGroupMap' });
      },
      accessibleScopeHierarchy: {},
      setAccessibleScopeHierarchy: (hierarchy) => {
        set({ accessibleScopeHierarchy: hierarchy }, false, { type: 'setAccessibleScopeHierarchy' });
      },
      accessibleGroupIds: [],
      setAccessibleGroupIds: (ids) => {
        set({ accessibleGroupIds: ids }, false, { type: 'setAccessibleGroupIds' });
      },
      accessibleLocationIds: [],
      setAccessibleLocationIds: (ids) => {
        set({ accessibleLocationIds: ids }, false, { type: 'setAccessibleLocationIds' });
      },
      accessibleOrgIds: [],
      setAccessibleOrgIds: (ids) => {
        set({ accessibleOrgIds: ids }, false, { type: 'setAccessibleOrgIds' });
      },
      isSingleTypeScope: false,
      setIsSingleTypeScope: (value) => {
        set({ isSingleTypeScope: value }, false, { type: 'setIsSingleTypeScope' });
      },
    }),
    { name: 'ScopeStore', trace: true }
  );

export const useLocationGroupStore = createScopeStore();
const useScopeShallowStore = createShallowStore(useLocationGroupStore);

let lastSentIdsToShell: string[] = [];

const useShellSideEffect = ({ selectedLocationIds }: { selectedLocationIds: string[] }) => {
  /* Send selected location ids to shell */
  if (shell.isShell && !isEqual(lastSentIdsToShell, selectedLocationIds) && selectedLocationIds.length !== 0) {
    lastSentIdsToShell = selectedLocationIds ?? [];

    if (shell.featureAvailability?.has('zustand-sync')) {
      /* Primary path: push via Zustand sync store (new shells with zustand-sync) */
      useShellLocationsSyncStore.getState().setSelectedLocationIds(selectedLocationIds ?? []);
    } else {
      /* Backward-compat: IPC emit for older shells without zustand-sync */
      shell.emit?.('send:locations', { type: 'locations', locations: selectedLocationIds ?? [] });
    }
  }
};

/**
 * Getter version of the app scope store - light wrapper around the Zustand store
 * This version is for backward compatibility and provides direct access to the store state (READ-ONLY)
 */
export const useAppScopeStore = () => {
  // Use shallow store to only subscribe to the properties we need
  const {
    locationData,
    groupData,
    orgData,
    selectedOrgId,
    selectedGroupId,
    forcedGroupId,
    selectedLocationIds,
    scopeHierarchy,
    accessibleLocationMap,
    accessibleGroupMap,
    accessibleScopeHierarchy,
    accessibleGroupIds,
    accessibleLocationIds,
    accessibleOrgIds,
    isSingleTypeScope,
  } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'selectedOrgId',
    'selectedGroupId',
    'forcedGroupId',
    'selectedLocationIds',
    'scopeHierarchy',
    'accessibleLocationMap',
    'accessibleGroupMap',
    'accessibleScopeHierarchy',
    'accessibleGroupIds',
    'accessibleLocationIds',
    'accessibleOrgIds',
    'isSingleTypeScope'
  );

  const getScopeName = useCallback(
    (scopeId: string, type?: 'location' | 'group' | 'org'): string => {
      if (type === 'location') {
        const location = locationData[scopeId] ?? accessibleLocationMap[scopeId];
        return location?.name ?? '';
      }
      if (type === 'group') {
        const group = groupData[scopeId] ?? accessibleGroupMap[scopeId];
        return group?.name ?? '';
      }
      if (type === 'org') {
        const org = orgData[scopeId];
        return org?.name ?? '';
      }

      const scope = accessibleLocationMap[scopeId] ?? accessibleGroupMap[scopeId] ?? orgData[scopeId];
      return scope?.name ?? '';
    },
    [locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap]
  );

  const groupInclusiveSelectedLocationIds = useMemo(() => {
    const set = new Set([...selectedLocationIds]);

    if (accessibleGroupIds.includes(selectedGroupId)) {
      set.add(selectedGroupId);
    }

    return [...set];
  }, [selectedLocationIds, accessibleGroupIds, selectedGroupId]);

  // Return only state properties (no setters) - memoized for performance
  return useMemo(
    () => ({
      // Basic state
      locationData,
      groupData,
      orgData,
      selectedOrgId,
      selectedGroupId,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      scopeHierarchy,

      // Computed state
      accessibleLocationData: accessibleLocationMap,
      accessibleGroupData: accessibleGroupMap,
      accessibleScopeHierarchy,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,

      // Utility functions
      getScopeName,
      // Keep backward compatibility alias
      getLocationName: getScopeName,
    }),
    [
      // Basic state dependencies
      locationData,
      groupData,
      orgData,
      selectedOrgId,
      selectedGroupId,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      scopeHierarchy,
      // Computed state dependencies
      accessibleLocationMap,
      accessibleGroupMap,
      accessibleScopeHierarchy,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,
      // Utility function dependencies
      getScopeName,
    ]
  );
};

/**
 * Setter version of the app scope store - complex wrapper with calculations and validation logic
 * This version includes setters with calculations and validation logic
 */
export const useAppScopeStoreWithSetters = () => {
  const {
    setLocationData,
    setGroupData,
    setOrgData,
    setSelectedGroupId,
    setSelectedLocationIds,
    setSelectedOrgId,
    setForcedGroupId,
    appendLocationData,
    appendGroupData,
    appendOrgData,
    setScopeHierarchy,
    appendScopeHierarchy,
    setAccessibleLocationMap,
    setAccessibleGroupMap,
    setAccessibleScopeHierarchy,
    setAccessibleGroupIds,
    setAccessibleLocationIds,
    setAccessibleOrgIds,
    setIsSingleTypeScope,
  } = useLocationGroupStore.getState();

  const setAllAccessLocationMap = useAllAccessScopeStore((state) => state.setAccessibleLocationMap);
  const setAllAccessGroupMap = useAllAccessScopeStore((state) => state.setAccessibleGroupMap);
  /**
   * We want these properties to be reactive.
   * We don't want to use the store directly because it will cause unnecessary re-renders.
   *
   * Components using this hook will rerender when the properties below are updated
   */
  const {
    locationData,
    groupData,
    orgData,
    selectedGroupId,
    selectedLocationIds,
    selectedOrgId,
    forcedGroupId,
    scopeHierarchy,
  } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'selectedGroupId',
    'selectedLocationIds',
    'selectedOrgId',
    'forcedGroupId',
    'scopeHierarchy'
  );

  const user = useRef(getUser());

  // Compute and set accessibleLocationMap into the store
  useEffect(() => {
    const accessibleLocationMap = Object.keys(locationData).reduce((acc, locationId) => {
      const location = locationData[locationId];
      if (location.accessible && location.active) {
        acc[locationId] = {
          ...location,
        };
      }
      return acc;
    }, {} as Record<string, WeaveLocation>);

    setAccessibleLocationMap(accessibleLocationMap);
    setAllAccessLocationMap(accessibleLocationMap);
  }, [locationData, setAccessibleLocationMap]);

  // Compute and set accessibleGroupMap into the store
  useEffect(() => {
    const accessibleGroupMap = Object.keys(groupData).reduce((acc, groupId) => {
      const group = groupData[groupId];
      if (group.accessible && group.active) {
        acc[groupId] = {
          ...group,
          children: group.children.filter((location) => location.accessible && location.active),
        };
      }
      return acc;
    }, {} as Record<string, WeaveLocationGroup>);

    setAccessibleGroupMap(accessibleGroupMap);
    setAllAccessGroupMap(accessibleGroupMap);
  }, [groupData, setAccessibleGroupMap]);

  // Get the current values from store for computing hierarchy
  const { accessibleLocationMap, accessibleGroupMap } = useLocationGroupStore.getState();

  // Compute and set accessibleScopeHierarchy into the store
  useEffect(() => {
    const accessibleScopeHierarchy = Object.keys(scopeHierarchy).reduce((acc, orgId) => {
      const org = scopeHierarchy[orgId];
      acc[orgId] = Object.keys(org).reduce((acc, groupId) => {
        const groupLocationIds = org[groupId];
        const isGroupAccessible = !!accessibleGroupMap[groupId];
        const accessibleLocationIdsInGroup = groupLocationIds.filter(
          (locationId) => !!accessibleLocationMap[locationId]
        );

        // Include the group in the accessible hierarchy if the group itself is accessible
        // or if it contains at least one accessible location.
        if (isGroupAccessible || accessibleLocationIdsInGroup.length > 0) {
          acc[groupId] = accessibleLocationIdsInGroup;
        }
        return acc;
      }, {} as Record<string, string[]>);
      return acc;
    }, {} as Record<string, Record<string, string[]>>);

    setAccessibleScopeHierarchy(accessibleScopeHierarchy);
  }, [scopeHierarchy, accessibleLocationMap, accessibleGroupMap, setAccessibleScopeHierarchy]);

  // Compute and set accessibleGroupIds into the store
  useEffect(() => {
    const accessibleGroupIds = Object.keys(accessibleGroupMap);
    setAccessibleGroupIds(accessibleGroupIds);
  }, [accessibleGroupMap, setAccessibleGroupIds]);

  // Compute and set accessibleLocationIds into the store
  useEffect(() => {
    const accessibleLocationIds = Object.keys(accessibleLocationMap);
    setAccessibleLocationIds(accessibleLocationIds);
  }, [accessibleLocationMap, setAccessibleLocationIds]);

  // Compute and set accessibleOrgIds into the store
  useEffect(() => {
    const accessibleOrgIds = Object.keys(orgData);
    setAccessibleOrgIds(accessibleOrgIds);
  }, [orgData, setAccessibleOrgIds]);

  // Compute and set isSingleTypeScope into the store
  useEffect(() => {
    const isSingleTypeScope = selectedLocationIds.length === 1 && groupData[forcedGroupId]?.locationType === 'single';
    setIsSingleTypeScope(isSingleTypeScope);
  }, [selectedLocationIds, groupData, forcedGroupId, setIsSingleTypeScope]);

  const _setSelectedOrgId = useCallback(
    (orgId: string) => {
      if (user.current?.userID) {
        setLoginData(user.current.userID, { latestOrganizationId: orgId });
      }
      setSelectedOrgId(orgId);
    },
    [setSelectedOrgId]
  );

  const _setSelectedGroupId = useCallback(
    (groupId: string, { skipValidation = false }: { skipValidation?: boolean } = {}) => {
      const isGroupAccessible = !!accessibleGroupMap[groupId];

      if (skipValidation || isGroupAccessible) {
        setSelectedGroupId(groupId);
        setForcedGroupId(groupId);
      } else {
        /**
         * If the group is not accessible, we still want to set the forcedGroupId.
         * There is potential for the group to not be accessible, but the locations within it are,
         * we still might need the groupId for the non-accessible group.
         */
        setSelectedGroupId('');
        setForcedGroupId(groupId);
      }
    },
    [accessibleGroupMap, setSelectedGroupId, setForcedGroupId]
  );

  const _setSelectedLocationIds = useCallback(
    (locationIds: string[], { skipValidation = false }: { skipValidation?: boolean } = {}) => {
      if (skipValidation) {
        setSelectedLocationIds(locationIds);
        if (user.current?.userID) {
          setLoginData(user.current.userID, { latestLocationIds: locationIds });
        }

        // This is a bad path to go down because we select a random location and set the groupId and orgId based on it
        // We're skipping validation now because DCA has multiple groups within the same org
        const referenceLocation = locationIds[0]
          ? // allow for parent location to be selected
            accessibleLocationMap[locationIds[0]] || accessibleGroupMap[locationIds[0]]
          : undefined;
        const groupId = referenceLocation?.groupId;
        const orgId = referenceLocation?.orgId;

        const actualGroupId = groupId ?? locationIds[0];

        if (actualGroupId) {
          _setSelectedGroupId(actualGroupId, { skipValidation: true });
          setForcedGroupId(actualGroupId);
        }
        if (orgId) {
          _setSelectedOrgId(orgId);
        }

        return;
      }

      // Location ids must be accessible
      if (locationIds.some((id) => !accessibleLocationMap[id])) {
        console.warn('Setting an invalid or inaccessible location. AppScopeStore selections have been reset.');
        setSelectedLocationIds([]);
        _setSelectedGroupId('');
        setForcedGroupId('');
        _setSelectedOrgId('');
        return;
      }

      const locations = locationIds.map((id) => accessibleLocationMap[id]);
      const groupIds = new Set(locations.map((location) => location.groupId).filter(Boolean));

      if (groupIds.size > 1) {
        console.warn('Locations must belong to the same group. AppScopeStore selections have been reset.');
        setSelectedLocationIds([]);
        _setSelectedGroupId('');
        setForcedGroupId('');
        _setSelectedOrgId('');
        return;
      }

      if (user.current?.userID) {
        setLoginData(user.current.userID, { latestLocationIds: locationIds });
      }

      /**
       * If we can determine the orgId and groupId from a selected location,
       * set them as well
       */
      const referenceLocation = locationIds[0] ? accessibleLocationMap[locationIds[0]] : undefined;
      const groupId = referenceLocation?.groupId;
      const orgId = referenceLocation?.orgId;

      if (groupId) {
        _setSelectedGroupId(groupId);
        setForcedGroupId(groupId);
      }

      if (orgId) {
        _setSelectedOrgId(orgId);
      }

      setSelectedLocationIds(locationIds);
    },
    [
      accessibleLocationMap,
      accessibleGroupMap,
      _setSelectedGroupId,
      _setSelectedOrgId,
      setSelectedLocationIds,
      setForcedGroupId,
    ]
  );

  useShellSideEffect({ selectedLocationIds });

  // Get current computed values from the store
  const {
    accessibleLocationMap: storeAccessibleLocationMap,
    accessibleGroupMap: storeAccessibleGroupMap,
    accessibleScopeHierarchy: storeAccessibleScopeHierarchy,
    accessibleGroupIds: storeAccessibleGroupIds,
    accessibleLocationIds: storeAccessibleLocationIds,
    accessibleOrgIds: storeAccessibleOrgIds,
    isSingleTypeScope: storeIsSingleTypeScope,
  } = useLocationGroupStore.getState();

  const getScopeName = useCallback(
    (scopeId: string, type?: 'location' | 'group' | 'org'): string => {
      if (type === 'location') {
        const location = locationData[scopeId] ?? storeAccessibleLocationMap[scopeId];
        return location?.name ?? '';
      }
      if (type === 'group') {
        const group = groupData[scopeId] ?? storeAccessibleGroupMap[scopeId];
        return group?.name ?? '';
      }
      if (type === 'org') {
        const org = orgData[scopeId];
        return org?.name ?? '';
      }

      const scope = storeAccessibleLocationMap[scopeId] ?? storeAccessibleGroupMap[scopeId] ?? orgData[scopeId];
      return scope?.name ?? '';
    },
    [locationData, groupData, orgData, storeAccessibleLocationMap, storeAccessibleGroupMap]
  );

  // To memoize this state, everything within it has to be memoized
  const state = useMemo(() => {
    return {
      accessibleGroupIds: storeAccessibleGroupIds,
      accessibleLocationIds: storeAccessibleLocationIds,
      accessibleOrgIds: storeAccessibleOrgIds,

      accessibleOrgData: orgData,
      /**
       * This is the source of truth for all location data.
       * It should only contain locations that the user has access to, never more than that.
       *
       * This completely depends on the input being passed to the store via `setScopeData`.
       */
      accessibleLocationData: storeAccessibleLocationMap,
      accessibleGroupData: storeAccessibleGroupMap,
      accessibleScopeHierarchy: storeAccessibleScopeHierarchy,
      locationData,
      groupData,

      setLocationData: setLocationData,
      setGroupData: setGroupData,
      setOrgData: setOrgData,

      appendAccessibleLocationData: appendLocationData,
      appendAccessibleGroupData: appendGroupData,
      appendAccessibleOrgData: appendOrgData,

      selectedLocationIds,
      selectedGroupId,
      selectedOrgId,
      /**
       * All `selected-*` properties have to be accessible. `forcedGroupId` is different in that it may be inaccessible to the user.
       * This property reflects the groupId of selected locations, even if the group itself is not accessible to the user.
       * No further data about the group is available from the store.
       *
       * Both `selectedGroupId` and `forcedGroupId` and derived from the `selectedLocationIds`.
       * `selectedGroupId` may or may not be set (depending on access), but `forcedGroupId` will always be set.
       */
      forcedGroupId,

      setSelectedGroupId: _setSelectedGroupId,
      // Don't expose this function
      // setForcedGroupId,
      setSelectedOrgId: _setSelectedOrgId,
      setSelectedLocationIds: _setSelectedLocationIds,

      scopeHierarchy,
      setScopeHierarchy,
      appendScopeHierarchy,

      isSingleTypeScope: storeIsSingleTypeScope,

      // Utility functions
      getScopeName,
      getLocationName: getScopeName,
    };
  }, [
    storeAccessibleGroupIds,
    storeAccessibleLocationIds,
    storeAccessibleOrgIds,
    orgData,
    storeAccessibleLocationMap,
    storeAccessibleGroupMap,
    storeAccessibleScopeHierarchy,
    locationData,
    groupData,
    setLocationData,
    setGroupData,
    setOrgData,
    appendLocationData,
    appendGroupData,
    appendOrgData,
    selectedLocationIds,
    selectedGroupId,
    selectedOrgId,
    forcedGroupId,
    _setSelectedGroupId,
    _setSelectedOrgId,
    _setSelectedLocationIds,
    scopeHierarchy,
    setScopeHierarchy,
    appendScopeHierarchy,
    storeIsSingleTypeScope,
    getScopeName,
  ]);

  return state;
};

/**
 * Run `callback(newLocationIds, oldLocationIds)` whenever the store's selectedLocationIds change only active while the component is mounted.
 */
export function useOnScopeChange(
  callback: (newLocationIds: string[] | undefined, prevLocationIds: string[] | undefined) => void,
  cleanup?: () => void
) {
  // keep a ref to the latest callback so subscribe only needs to be set up once
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // subscribe to changes of selectedLocationIds
    const unsubscribe = useLocationGroupStore.subscribe(
      (state) => state.selectedLocationIds,
      (newLocationIds, prevLocationIds) => {
        callbackRef.current(newLocationIds, prevLocationIds);
      }
    );
    return () => {
      // unsubscribe from changes of selectedLocationIds
      unsubscribe();
      if (cleanup) {
        cleanup();
      }
    };
  }, [cleanup]);
}
